import { DocumentService } from 'src/app/services/document.service';
import { faShare, faCaretRight, faCaretLeft, faImages, faTimesCircle, faPlus, faMinus, faSearchPlus, faSearchMinus, faUndoAlt, faReply, faXmark } from '@fortawesome/free-solid-svg-icons';
import { Component, Input, OnInit, ViewChild, ElementRef, ViewContainerRef, TemplateRef, OnDestroy, EventEmitter, Output } from '@angular/core';
import { Router, RouterEvent, NavigationStart } from '@angular/router';
import { ComponentPortal, CdkPortal } from '@angular/cdk/portal';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { cloneDeep, forEach } from 'lodash';
import { __importDefault } from 'tslib';
import Relationship from 'src/app/models/relationship';
import OpenEnrollment from 'src/app/models/openEnrollment';
import Document from 'src/app/models/document';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import Subscriber from 'src/app/models/subscriber';
import SpecialOpenEnrollment from 'src/app/models/specialOpenEnrollment';
import { AccessLevel, UserTypeCode, CoreService } from 'src/app/services/core.service';
import { Lookups, LookupType } from 'src/app/decorators/lookups.decorator';

@UntilDestroy()
@Component({
  selector: 'generic-document-viewer',
  templateUrl: 'genericDocumentViewer.component.html',
  styleUrls: [],
  providers: [],
})
@Lookups(LookupType.UserAccessLevel, LookupType.UserRole, LookupType.Organization, LookupType.UserType)
export class GenericDocumentViewerComponent implements OnInit, OnDestroy {
  private overlayRef: OverlayRef = null;
  page = 1;
  zoom = 1;
  rotation = 0;
  reset = true;
  now = new Date();
  icons = {
    faImages,
    faTimesCircle,
    faPlus,
    faMinus,
    faSearchPlus,
    faSearchMinus,
    faUndoAlt,
    faReply,
    faShare,
    faCaretRight,
    faCaretLeft,
    faXmark
  };
  @Input() showDocumentImage = true;
  @Input() documents: Document[];
  @Input() documentName?: string;
  @Input() associatedRelationship?: Relationship;
  @Input() openEnrollment: OpenEnrollment;
  @Input() canDeleteDocuments: boolean = false;
  isHCA: boolean = false;
  deleteDocumentId: string = null;
  @ViewChild('pdfViewer') private pdfViewer: ElementRef;
  @ViewChild('pdfTemplateOverlay') private pdfTemplateOverlay: TemplateRef<any>;
  @Output() deleteDocument: EventEmitter<string> = new EventEmitter<string>();
  viewerOptions: any = {
    navbar: false,
    size: 'small',
    toolbar: {
      zoomIn: 4,
      zoomOut: 4,
      oneToOne: 4,
      reset: 4,
      play: {
        show: 4,
        size: 'large',
      },
      rotateLeft: 4,
      rotateRight: 4,
      flipHorizontal: 4,
      flipVertical: 4,
    },
  };

  constructor(private coreService: CoreService, private overlay: Overlay, private viewContainer: ViewContainerRef, private router: Router, private documentService: DocumentService) {}

  ngOnInit(): void {
    this.router.events.pipe(untilDestroyed(this)).subscribe((e: RouterEvent) => {
      if (e instanceof NavigationStart) {
        this.closeDoc();
      }
    });
    this.isHCA = this.coreService.systemUserHasAccess(AccessLevel.ReadOnly, UserTypeCode.HCA);
  }

  ngOnDestroy(): void {
    this.closeDoc();
  }

  showDocument(doc): void {
    doc.showDocument = !doc.showDocument;
    if (doc.showDocument) {
      this.resetDoc();
      this.page = 1;
      if (!this.overlayRef) {
        this.overlayRef = this.overlay.create();
      }
      // Create ComponentPortal that can be attached to a PortalHost
      const templateOverlay = new CdkPortal(this.pdfTemplateOverlay, this.viewContainer);
      templateOverlay.context = { document: doc };
      const component = this.overlayRef.attach(templateOverlay); // Attach ComponentPortal to PortalHost

      // set accessibility settings to prevent tabbing behind overlay
      let viewer = document.getElementsByClassName('cdk-overlay-container')[0];
      this.setAccessibilityAttributes(viewer);
      this.trapFocus(viewer);
    }
  }

  closeDoc(): void {
    if (!!this.overlayRef) {
      this.overlayRef.detach();
    }
    forEach(this.documents, (d) => (d.showDocument = false));
    this.resetDoc();
  }

  // Left or Right
  rotateDoc(direction: string): void {
    if (direction === 'left') {
      if (this.rotation === 0) {
        this.rotation = 270;
      } else {
        this.rotation = this.rotation - 90;
      }
    } else {
      if (this.rotation >= 270) {
        this.rotation = 0;
      } else {
        this.rotation = this.rotation + 90;
      }
    }
  }

  // Add or Minus
  zoomDoc(inOrOut: string): void {
    this.zoom = inOrOut === 'add' ? this.zoom + 0.5 : this.zoom - 0.5;
  }

  resetDoc(): void {
    this.zoom = 1;
    this.rotation = 0;
    this.page = 1;
  }

  pageNext(): void {
    this.page++;
  }

  pagePrevious(): void {
    if (this.page > 1) {
      this.page--;
    }
  }

  deleteDoc() {
    this.deleteDocument.emit(this.deleteDocumentId);
    this.deleteDocumentId = null;
  }

  confirmDeleteDoc(documentId) {
    this.deleteDocumentId = documentId;
  }

  openImage(documentId) {
    const imageViewer = document.getElementById(documentId);
    if (imageViewer) {
      imageViewer.click();
    }
  }

  // called by ngxViewer when the image overlay is shown
  onViewerShown(viewerEvent : any) {
    // set accessibility settings to prevent tabbing behind overlay when ngx-viewer opens
    let viewer = document.getElementById('viewer0');
    this.setAccessibilityAttributes(viewer);
    this.trapFocus(viewer);
  }

  // set aria attributes for accessibility
  setAccessibilityAttributes(viewer : HTMLElement | Element) {
    if (viewer) {
      viewer.setAttribute("aria-modal", "true");
      viewer.setAttribute("role", "dialog");
      viewer.setAttribute("aria-label", 'Document Viewer');
      viewer.setAttribute("aria-labelledby", 'dialogTitle');
      viewer.setAttribute("aria-describedby", "dialogDesc");
    }
  }

  // Trap focus inside of a overlay/modal. Screen readers were tabbing through behind the document viewers, making it difficult to get through the document viewer.
  trapFocus(element : HTMLElement | Element) {
    var focusableEls = element.querySelectorAll<HTMLInputElement | HTMLElement>('a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])');
    var firstFocusableEl = focusableEls[0];  
    var lastFocusableEl = focusableEls[focusableEls.length - 1];

    if (firstFocusableEl) {
      firstFocusableEl.focus();
    }

    element.addEventListener('keydown', function(e : KeyboardEvent) {
      var isTabPressed = (e.key === 'Tab');

      if (!isTabPressed) { 
        return; 
      }

      if (e.shiftKey) /* shift + tab */ {
        if (document.activeElement === firstFocusableEl) {
          lastFocusableEl.focus();
            e.preventDefault();
          }
        } else /* tab */ {
        if (document.activeElement === lastFocusableEl) {
          firstFocusableEl.focus();
            e.preventDefault();
          }
        }
    });
  }

}
